import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/common/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import mimeToIcon from '../mimeToIcon';
import classList from 'flarum/common/utils/classList';
import Button from 'flarum/common/components/Button';
import DisplayFile from './DisplayFile';
import Alert from 'flarum/common/components/Alert';
import extractText from 'flarum/common/utils/extractText';
import Icon from 'flarum/common/components/Icon';
export default class AbstractFileList extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "user", null);
    _defineProperty(this, "inModal", void 0);
    _defineProperty(this, "restrictFileType", void 0);
    _defineProperty(this, "downloadOnClick", void 0);
    _defineProperty(this, "filesBeingHidden", void 0);
    _defineProperty(this, "fileState", void 0);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.inModal = this.attrs.selectable;
    this.restrictFileType = this.attrs.restrictFileType || null;
    this.downloadOnClick = this.attrs.downloadOnClick || false;
    this.filesBeingHidden = [];
    this.fileState = this.attrs.fileState;
    this.loadFileList();
  }
  view() {
    return m("div", {
      className: "SharedFileList fof-upload-file-list",
      "aria-live": "polite"
    }, this.isLoading() && this.fileCollection().length === 0 && m("div", {
      className: 'fof-upload-loading'
    }, app.translator.trans('fof-upload.lib.file_list.loading'), m(LoadingIndicator, null)), !this.isLoading() && this.fileCollection().length === 0 && this.fileState.hasLoadError() && m("div", {
      className: "Placeholder"
    }, m("p", {
      className: "fof-upload-empty"
    }, app.translator.trans('fof-upload.lib.file_list.load_error'))), !this.isLoading() && this.fileCollection().length === 0 && !this.fileState.hasLoadError() && m("div", {
      className: "Placeholder"
    }, m("p", {
      className: "fof-upload-empty"
    }, app.translator.trans('fof-upload.lib.file_list.empty'))), m("ul", null, this.fileCollection().map(file => {
      const fileIcon = mimeToIcon(file.type());
      const fileSelectable = this.restrictFileType ? this.isSelectable(file) : true;
      const fileClassNames = classList(['fof-file',
      // File is image
      fileIcon === 'far fa-file-image' && 'fof-file-type-image',
      // File is selected
      this.attrs.selectedFiles && this.attrs.selectedFiles.indexOf(file.id() ?? '') >= 0 && 'fof-file-selected']);
      const isFileHiding = this.filesBeingHidden.includes(file.uuid());
      return m("li", {
        "aria-busy": isFileHiding,
        key: file.uuid()
      }, m(DisplayFile, {
        file: file,
        fileSelectable: fileSelectable,
        isSelected: this.attrs.selectedFiles && this.attrs.selectedFiles.indexOf(file.id() ?? '') >= 0,
        fileClassNames: fileClassNames,
        isFileHiding: isFileHiding,
        onHide: this.hideFile.bind(this),
        onFileClick: this.onFileClick.bind(this),
        user: this.attrs.user,
        onDelete: this.onDelete.bind(this)
      }));
    })), this.hasMoreResults() && m("div", {
      className: 'fof-load-more-files'
    }, m(Button, {
      className: 'Button Button--primary',
      disabled: this.isLoading(),
      loading: this.isLoading(),
      onclick: () => this.loadMore()
    }, app.translator.trans('fof-upload.lib.file_list.load_more_files_btn'))));
  }
  onDelete(file) {
    if (this.attrs.onDelete) {
      this.attrs.onDelete(file);
    }
  }

  // Common methods like onFileClick, isSelectable, hideFile...

  onFileClick(file) {
    // Custom functionality
    if (this.attrs.onFileSelect) {
      this.attrs.onFileSelect(file);
      return;
    }

    // Download on click
    if (this.attrs.downloadOnClick) {
      window.open(file.url());
      return;
    }
  }
  isSelectable(file) {
    const fileType = file.type();

    // Custom defined file types
    if (Array.isArray(this.restrictFileType)) {
      return this.restrictFileType.indexOf(fileType) >= 0;
    }

    // Image
    else if (this.restrictFileType === 'image') {
      return fileType.includes('image/');
    }

    // Audio
    else if (this.restrictFileType === 'audio') {
      return fileType.includes('audio/');
    }

    // Video
    else if (this.restrictFileType === 'video') {
      return fileType.includes('video/');
    }
    return false;
  }

  /**
   * Begins the hiding process for a file.
   *
   * - Shows a native confirmation dialog
   * - If confirmed, sends AJAX request to the hide file API
   */
  async hideFile(file) {
    const uuid = file.uuid();
    if (this.filesBeingHidden.includes(uuid)) return;
    this.filesBeingHidden.push(uuid);
    const transPrefix = file.isShared() ? 'fof-upload.lib.file_list.hide_shared_file' : 'fof-upload.lib.file_list.hide_file';
    const confirmToggleHide = confirm(extractText(app.translator.trans(file.hidden() ? `${transPrefix}.show_confirmation` : `${transPrefix}.hide_confirmation`, {
      fileName: file.baseName()
    })));
    if (confirmToggleHide) {
      try {
        const filePayload = await app.request({
          method: 'PATCH',
          url: `${app.forum.attribute('apiUrl')}/fof/upload/hide`,
          body: {
            uuid
          }
        });
        app.store.pushPayload(filePayload);
        m.redraw();
        app.alerts.show(Alert, {
          type: 'success'
        }, [m(Icon, {
          name: file.hidden() ? 'fas fa-eye-slash' : 'fas fa-eye'
        }), ' ', app.translator.trans(file?.hidden() ? `${transPrefix}.hide_success` : `${transPrefix}.show_success`)]);
        if (this.fileState.user) {
          const index = this.fileState.files.findIndex(file => uuid === file.uuid());
          this.fileState.files.splice(index, 1);
        }
      } catch (error) {
        app.alerts.show(Alert, {
          type: 'error'
        }, app.translator.trans(file?.hidden() ? `${transPrefix}.hide_fail` : `${transPrefix}.show_fail`, {
          fileName: file.baseName()
        }));
      } finally {
        // Remove file from hiding list
        const i = this.filesBeingHidden.indexOf(uuid);
        this.filesBeingHidden.splice(i, 1);
      }
    }
  }
}
flarum.reg.add('fof-upload', 'common/components/AbstractFIleList', AbstractFileList);